/**
 * G5 (THE-GRID-NOTES) — The Grid remembers its place.
 *
 * Date, Day/Week mode, trade filter, and the full-day toggle persist per
 * user (localStorage) and in the URL. Top-nav "THE GRID" returns to the
 * LAST state instead of resetting to today + defaults.
 *
 * Judgment call carried over from the earlier stale-date fix (the board
 * used to open stuck on a past date from a previous session): the DATE
 * only restores when it was saved on the same local calendar day. A
 * dispatcher bouncing job → grid all afternoon lands back on the day
 * they were working; opening the app tomorrow morning starts on today.
 * View / trade / full-day restore unconditionally — they're preferences,
 * not positions.
 */
import { localDateKey } from "@/lib/datetime";

export interface GridState {
    date: string;
    view: "day" | "week";
    tradeId: string;
    fullDay: boolean;
    /** #975 "Fit day" — squeeze the day's actual work span into the viewport. */
    fitDay: boolean;
    savedAt: number;
}

const STORAGE_KEY = "grid.lastState.v1";

export function saveGridState(state: Omit<GridState, "savedAt">, now: Date = new Date()): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, savedAt: now.getTime() }));
    } catch {
        // Storage full / privacy mode — the grid just won't remember.
    }
}

export function loadGridState(): GridState | null {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<GridState>;
        if (typeof parsed.date !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(parsed.date)) return null;
        return {
            date: parsed.date,
            view: parsed.view === "week" ? "week" : "day",
            tradeId: typeof parsed.tradeId === "string" ? parsed.tradeId : "",
            fullDay: parsed.fullDay === true,
            fitDay: parsed.fitDay === true,
            savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0,
        };
    } catch {
        return null;
    }
}

/**
 * The date to restore from a saved state, or null when it should not be
 * restored (saved on a different calendar day — start on today instead).
 */
export function restorableDate(state: GridState | null, now: Date = new Date()): string | null {
    if (!state || !state.savedAt) return null;
    return localDateKey(new Date(state.savedAt)) === localDateKey(now) ? state.date : null;
}

// ── G5 + #762: the day board remembers WHERE you were looking ─────────────
//
// The swimlane's horizontal scroll position persists per date in
// sessionStorage (per tab, like browser history): a Back-navigation return
// lands exactly where the dispatcher left off instead of re-jumping to
// "now". A fresh tab has no saved position, so it opens at now.

const SCROLL_KEY = "grid.dayScroll.v1";

export function saveDayScroll(date: string, left: number): void {
    try {
        window.sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ date, left }));
    } catch {
        // Storage unavailable — the board just won't remember its place.
    }
}

export function loadDayScroll(date: string): number | null {
    try {
        const raw = window.sessionStorage.getItem(SCROLL_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { date?: unknown; left?: unknown };
        if (parsed.date !== date) return null;
        return typeof parsed.left === "number" && Number.isFinite(parsed.left) && parsed.left >= 0 ? parsed.left : null;
    } catch {
        return null;
    }
}
